'use client';

// ═══════════════════════════════════════════════════════════════
// Theme Data Provider
// Global context for theme data (site, settings, products, etc.)
// With tenant-aware routing support
// ═══════════════════════════════════════════════════════════════

import { createContext, useContext, ReactNode, useMemo } from 'react';
import { CurrencyProvider } from './hooks/useCurrency';
import type {
    SiteData,
    Product,
    Collection
} from './theme-contract';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/**
 * Route information for the current view
 */
export interface RouteData {
    view: string;
    slug?: string | null;
    query?: string | null;
}

/**
 * Theme data context shape
 * Contains all data passed from server to theme
 */
export interface ThemeData {
    /** Site information */
    site: SiteData;

    /** Theme customization settings */
    settings: Record<string, unknown>;

    /** Current route/view */
    route: RouteData;

    /** Base path for tenant routing (e.g., "/store/salah" or "" for custom domain) */
    basePath: string;

    /** Pre-fetched products (for home page, etc.) */
    initialProducts?: Product[];

    /** Pre-fetched collections */
    initialCollections?: Collection[];

    /** Currently viewed product (for product page) */
    currentProduct?: Product | null;

    /** Currently viewed collection (for collection page) */
    currentCollection?: Collection | null;
}

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeData | null>(null);

// ─────────────────────────────────────────────────────────────
// Provider Component
// ─────────────────────────────────────────────────────────────

interface ThemeProviderProps {
    children: ReactNode;
    data: ThemeData;
}

/**
 * ThemeProvider wraps the theme and provides access to global data
 * Should be used at the root of each theme
 */
export function ThemeProvider({ children, data }: ThemeProviderProps) {
    // Memoize to prevent unnecessary re-renders
    const value = useMemo(() => data, [data]);

    // Extract currency config from site settings
    const siteSettings = data.site?.settings || {};
    const baseCurrency = (siteSettings as any)?.currency || 'DZD';
    const enabledCurrencies = (siteSettings as any)?.enabled_currencies || [baseCurrency];
    const autoDetect = (siteSettings as any)?.auto_detect_currency || false;

    return (
        <ThemeContext.Provider value={value}>
            <CurrencyProvider
                baseCurrency={baseCurrency}
                enabledCurrencies={enabledCurrencies}
                autoDetect={autoDetect}
            >
                {children}
            </CurrencyProvider>
        </ThemeContext.Provider>
    );
}

// ─────────────────────────────────────────────────────────────
// Hook - Access Full Context
// ─────────────────────────────────────────────────────────────

/**
 * Access the full theme context
 * Must be used within ThemeProvider
 */
export function useThemeContext(): ThemeData {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error(
            'useThemeContext must be used within a ThemeProvider. ' +
            'Make sure your theme wraps content with <ThemeProvider>.'
        );
    }

    return context;
}

// ─────────────────────────────────────────────────────────────
// Convenience Hooks
// ─────────────────────────────────────────────────────────────

/**
 * Get site data
 */
export function useSite(): SiteData {
    const { site } = useThemeContext();
    return site;
}

/**
 * Get current route
 */
export function useRoute(): RouteData {
    const { route } = useThemeContext();
    return route;
}

/**
 * Get base path for tenant-aware routing
 */
export function useBasePath(): string {
    const { basePath } = useThemeContext();
    return basePath;
}

export default ThemeProvider;
